// =============================================================================
// MeuDriver — Student Payment History
//
// Lê o livro de cobranças (`payment_charges`): aulas, pacotes, vales-presente
// e mensalidades do Premium, por Pix (Mercado Pago) ou cartão (Asaas). Abaixo,
// "Meus vales" lista os vales que o aluno comprou ou recebeu, com o código —
// assim quem comprou consegue repassar o código mesmo se o e-mail falhar.
// =============================================================================

import { Link } from 'react-router-dom'
import {
  CreditCard, ArrowLeft, CheckCircle, Clock, XCircle, ExternalLink, Download, Gift, Copy, RotateCcw,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import { downloadCsv, formatDateTimeBR, formatBRL } from '@/lib/csv'
import { toast } from 'sonner'

// ─── Types ────────────────────────────────────────────────────────────────────

type ChargeKind = 'booking' | 'package' | 'gift_card' | 'premium' | 'pro' | 'wallet'

interface ChargeRow {
  id: string
  kind: ChargeKind
  reference_id: string
  status: string
  amount: number
  method: 'pix' | 'credit_card'
  description: string | null
  paid_at: string | null
  refunded_at: string | null
  expires_at: string | null
  created_at: string
  access_token: string
}

interface GiftCardRow {
  id: string
  code: string
  amount: number
  remaining: number
  status: string
  recipient_email: string | null
  purchased_by: string | null
  expires_at: string | null
  created_at: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const KIND_LABEL: Record<ChargeKind, string> = {
  booking: 'Aula',
  package: 'Pacote de aulas',
  gift_card: 'Vale-presente',
  premium: 'MeuDriver Premium — mensalidade',
  pro: 'MeuDriver Pro — mensalidade',
  wallet: 'Recarga de saldo',
}

/** Tentativas que não viraram pagamento (Pix vencido, recusado) não entram. */
function isVisible(c: ChargeRow): boolean {
  if (['approved', 'refunded', 'charged_back'].includes(c.status)) return true
  return c.status === 'pending' && !!c.expires_at && parseISO(c.expires_at).getTime() > Date.now()
}

function statusIcon(status: string) {
  if (status === 'approved') return <CheckCircle className="h-4 w-4 text-green-500" />
  if (status === 'pending') return <Clock className="h-4 w-4 text-amber-500" />
  if (status === 'refunded') return <RotateCcw className="h-4 w-4 text-gray-400" />
  return <XCircle className="h-4 w-4 text-red-400" />
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    approved: 'Pago',
    pending: 'Aguardando pagamento',
    refunded: 'Estornado',
    charged_back: 'Contestado',
  }
  return map[status] ?? status
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'approved') return 'default'
  if (status === 'pending') return 'secondary'
  if (status === 'refunded') return 'outline'
  return 'destructive'
}

function methodLabel(method: ChargeRow['method']): string {
  return method === 'pix' ? 'Pix' : 'Cartão de crédito'
}

const GIFT_STATUS: Record<string, string> = {
  active: 'Disponível',
  partially_used: 'Usado em parte',
  fully_used: 'Usado',
  refunded: 'Estornado',
  expired: 'Vencido',
}

async function copyCode(code: string) {
  try {
    await navigator.clipboard.writeText(code)
    toast.success('Código copiado.')
  } catch {
    toast.error('Não foi possível copiar. Selecione o código e copie manualmente.')
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StudentPayments() {
  const { user } = useAuth()

  const { data: charges, isLoading } = useQuery<ChargeRow[]>({
    queryKey: ['student-charges', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_charges')
        .select('id, kind, reference_id, status, amount, method, description, paid_at, refunded_at, expires_at, created_at, access_token')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return ((data ?? []) as ChargeRow[])
        .map((c) => ({ ...c, amount: Number(c.amount) }))
        .filter(isVisible)
    },
    staleTime: 1000 * 60,
  })

  const { data: giftCards, isLoading: giftLoading } = useQuery<GiftCardRow[]>({
    queryKey: ['student-gift-cards', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gift_cards')
        .select('id, code, amount, remaining, status, recipient_email, purchased_by, expires_at, created_at')
        .neq('status', 'pending_payment')
        .order('created_at', { ascending: false })
      if (error) throw error
      return ((data ?? []) as GiftCardRow[]).map((g) => ({
        ...g,
        amount: Number(g.amount),
        remaining: Number(g.remaining),
      }))
    },
    staleTime: 1000 * 60,
  })

  const totalSpent =
    charges?.filter((c) => c.status === 'approved').reduce((s, c) => s + c.amount, 0) ?? 0

  function handleExportCsv() {
    if (!charges || charges.length === 0) {
      toast.info('Sem pagamentos para exportar.')
      return
    }
    const today = new Date().toISOString().slice(0, 10)
    downloadCsv(
      `meudriver-pagamentos-${today}.csv`,
      charges,
      [
        { key: 'paid_at', label: 'Data do pagamento', map: (v) => formatDateTimeBR(v) },
        { key: 'kind', label: 'Item', map: (v) => KIND_LABEL[v as ChargeKind] ?? String(v) },
        { key: 'description', label: 'Descrição', map: (v) => (v as string | null) ?? '' },
        { key: 'method', label: 'Meio de pagamento', map: (v) => methodLabel(v as ChargeRow['method']) },
        { key: 'amount', label: 'Valor', map: (v) => formatBRL(v) },
        { key: 'status', label: 'Status', map: (v) => statusLabel(v as string) },
      ],
    )
    toast.success('Extrato exportado.')
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to="/aluno/dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Histórico de pagamentos
          </h1>
          {charges && charges.length > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {charges.length} transaç{charges.length > 1 ? 'ões' : 'ão'}
            </p>
          )}
        </div>
        {totalSpent > 0 && (
          <div className="shrink-0 text-right">
            <p className="text-xl font-bold text-gray-900">{formatCurrency(totalSpent)}</p>
            <p className="text-xs text-muted-foreground">total investido</p>
          </div>
        )}
        {charges && charges.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="shrink-0 gap-1"
            title="Exportar histórico em CSV"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </Button>
        )}
      </div>

      {/* Pagamentos */}
      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : !charges || charges.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
              <CreditCard className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-700">Nenhum pagamento ainda</p>
            <p className="text-sm text-muted-foreground mt-1">
              Aulas, pacotes, vales-presente e assinaturas aparecerão aqui.
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/instrutores">Encontrar instrutores</Link>
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {charges.map((c) => {
              const when = c.paid_at ?? c.created_at
              const whenLabel = format(parseISO(when), "d/MM/yy 'às' HH:mm", { locale: ptBR })
              const pendente = c.status === 'pending'
              return (
                <div
                  key={c.id}
                  className="flex items-start gap-4 px-4 sm:px-6 py-4 hover:bg-muted/20 transition-colors"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100">
                    {statusIcon(c.status)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{KIND_LABEL[c.kind]}</p>
                        {c.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {methodLabel(c.method)} · {whenLabel}
                        </p>
                      </div>
                      <div className="shrink-0 text-right space-y-1">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(c.amount)}</p>
                        <Badge variant={statusVariant(c.status)} className="text-[10px] px-2 py-0.5">
                          {statusLabel(c.status)}
                        </Badge>
                      </div>
                    </div>
                    {pendente && c.method === 'pix' && (
                      <Button asChild size="sm" variant="outline" className="mt-2 h-7 text-xs">
                        <Link to={`/pagamento/${c.id}?t=${c.access_token}`}>Pagar com Pix</Link>
                      </Button>
                    )}
                  </div>

                  {c.kind === 'booking' && (
                    <Link
                      to={`/aluno/aulas/${c.reference_id}`}
                      className="shrink-0 text-muted-foreground hover:text-primary transition-colors mt-1"
                      title="Ver aula"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Meus vales */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Gift className="h-4 w-4 text-primary" />
            Meus vales-presente
          </h2>
          <Button asChild variant="ghost" size="sm" className="text-xs">
            <Link to="/presentes">Comprar vale</Link>
          </Button>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
          {giftLoading ? (
            <div className="px-6 py-4"><Skeleton className="h-10 w-full" /></div>
          ) : !giftCards || giftCards.length === 0 ? (
            <p className="px-6 py-6 text-sm text-muted-foreground text-center">
              Nenhum vale comprado ou recebido.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {giftCards.map((g) => {
                const comprei = g.purchased_by === user?.id
                const validade = g.expires_at
                  ? format(parseISO(g.expires_at), "d 'de' MMM 'de' yyyy", { locale: ptBR })
                  : null
                return (
                  <div key={g.id} className="px-4 sm:px-6 py-4 flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono font-semibold tracking-wide">{g.code}</code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => copyCode(g.code)}
                          title="Copiar código"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {comprei
                          ? `Comprado por você${g.recipient_email ? ` para ${g.recipient_email}` : ''}`
                          : 'Recebido de presente'}
                        {validade && ` · válido até ${validade}`}
                      </p>
                      {g.status === 'active' || g.status === 'partially_used' ? (
                        <p className="text-xs text-muted-foreground">
                          Use o código no último passo do agendamento de uma aula.
                        </p>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-right space-y-1">
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(g.remaining)}</p>
                      <p className="text-[11px] text-muted-foreground">de {formatCurrency(g.amount)}</p>
                      <Badge variant="outline" className="text-[10px] px-2 py-0.5">
                        {GIFT_STATUS[g.status] ?? g.status}
                      </Badge>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
